import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Users, PieChart, LogOut, FileText, Settings, Building2, ChevronDown, Menu, X, HardHat, Target, Layers, Boxes } from 'lucide-react';
import { ALL_ORGS } from '../types';
import { useAuth, useData } from '../App';
import { orgDisplayName } from '../utils/format';

export const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, logout } = useAuth();
  const { state, switchOrganization } = useData();
  const navigate = useNavigate();
  const location = useLocation(); // Чтобы закрывать меню при переходе
  
  const [isOrgMenuOpen, setIsOrgMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const orgMenuRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Закрываем меню при клике снаружи
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (orgMenuRef.current && !orgMenuRef.current.contains(event.target as Node)) {
        setIsOrgMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Закрываем мобильное меню при смене страницы
  useEffect(() => {
      setIsMobileMenuOpen(false);
  }, [location]);

  const currentOrg = state.organizations.find(o => o.id === state.currentOrganizationId);

  const navItemClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-2 px-3 py-2 rounded-md transition-colors text-sm font-medium ${
      isActive ? 'bg-accent/10 text-accent' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
    }`;

  // Ссылки меню вынесли в массив, чтобы не дублировать
  const navLinks = [
      { to: "/", label: "Операции", icon: <LayoutDashboard size={18} />, end: true },
      { to: "/directories", label: "Справочники", icon: <Users size={18} /> },
      { to: "/projects", label: "Объекты", icon: <HardHat size={18} /> },
      { to: "/volumes", label: "Объёмы", icon: <Boxes size={18} /> },
      { to: "/planning", label: "Планирование", icon: <Target size={18} /> },
      ...(user?.role === 'ADMIN' ? [{ to: "/reports", label: "Отчеты", icon: <PieChart size={18} /> }] : []),
      { to: "/profile", label: "Профиль", icon: <Settings size={18} /> },
  ];

  return (
    <div className="flex flex-col h-screen bg-slate-50">
      {/* Top Navigation Bar */}
      <header className="bg-white border-b border-slate-200 z-20 sticky top-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Одна flex-строка: меню не сжимается, переключатель организаций ужимается и обрезает название */}
          <div className="flex items-center gap-3 h-16">
            
            {/* ЛЕВАЯ ЧАСТЬ: Лого + Меню Десктоп */}
            <div className="flex items-center gap-3 lg:gap-6 flex-shrink-0">
              {/* Mobile Menu Button */}
              <button 
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 text-slate-500 hover:bg-slate-100 rounded-md"
              >
                  {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>

              {/* Logo */}
              <div className="flex-shrink-0 flex items-center gap-2 text-accent" title="Money Bamboney">
                <FileText className="h-8 w-8" />
                <span className="text-xl font-bold text-slate-900 hidden sm:block md:hidden">Money Bamboney</span>
              </div>

              {/* Desktop Navigation */}
              {/* Подписи — от 1280 px, уже только иконки с подсказкой */}
              <nav className="hidden md:flex flex-shrink-0 gap-1">
                {navLinks.map(link => (
                    <NavLink key={link.to} to={link.to} end={link.end} className={navItemClass} title={link.label} aria-label={link.label}>
                        {link.icon}
                        <span className="hidden xl:inline whitespace-nowrap">{link.label}</span>
                    </NavLink>
                ))}
              </nav>
            </div>

            {/* ПРАВАЯ ЧАСТЬ: Организация + Юзер */}
            <div className="flex items-center gap-2 sm:gap-4 min-w-0 ml-auto">
              
              {/* Organization Switcher: список открывается поверх (absolute), закрытый не занимает места */}
              <div className="relative min-w-0" ref={orgMenuRef} data-testid="org-switcher">
                  <button 
                    onClick={() => setIsOrgMenuOpen(!isOrgMenuOpen)}
                    title={state.currentOrganizationId === ALL_ORGS ? 'Все организации' : orgDisplayName(currentOrg)}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded-lg border transition-colors bg-white w-full max-w-[160px] sm:max-w-[220px] ${isOrgMenuOpen ? 'border-accent ring-1 ring-accent' : 'border-slate-200 hover:bg-slate-50'}`}
                  >
                      <div className="p-1 rounded bg-blue-50 text-blue-600 flex-shrink-0">
                          <Building2 size={16} />
                      </div>
                      <div className="flex flex-col items-start overflow-hidden min-w-0 flex-1">
                          {/* Убрали label "Организация" для экономии места */}
                          <span className="text-xs font-bold text-slate-800 truncate w-full text-left leading-tight">
                              {state.currentOrganizationId === ALL_ORGS ? 'Все организации' : orgDisplayName(currentOrg) || (state.organizations.length ? 'Выбрать...' : '…')}
                          </span>
                      </div>
                      <ChevronDown size={14} className={`text-slate-400 flex-shrink-0 transition-transform ${isOrgMenuOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {/* Dropdown */}
                  {isOrgMenuOpen && (
                    <div className="absolute right-0 top-full mt-1 w-64 bg-white rounded-lg shadow-xl border border-slate-100 z-50 overflow-hidden">
                        <div className="max-h-64 overflow-y-auto">
                            {state.organizations.length > 1 && (
                                <button onClick={() => { switchOrganization(ALL_ORGS); setIsOrgMenuOpen(false); }}
                                  className={`w-full text-left px-4 py-3 text-sm flex items-center gap-3 hover:bg-slate-50 border-b border-slate-100 ${state.currentOrganizationId === ALL_ORGS ? 'bg-blue-50/50 text-blue-700' : 'text-slate-700'}`}>
                                    <Layers size={14} className="flex-shrink-0" />
                                    <span className="font-bold">Все организации</span>
                                </button>
                            )}
                            {state.organizations.map(org => (
                                <button
                                  key={org.id}
                                  onClick={() => {
                                    switchOrganization(org.id);
                                    setIsOrgMenuOpen(false);
                                  }}
                                  className={`w-full text-left px-4 py-3 text-sm flex items-center gap-3 hover:bg-slate-50 border-b border-slate-50 last:border-0 ${org.id === state.currentOrganizationId ? 'bg-blue-50/50 text-blue-700' : 'text-slate-700'}`}
                                >
                                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${org.id === state.currentOrganizationId ? 'bg-blue-500' : 'bg-slate-300'}`} />
                                    <div className="flex flex-col min-w-0">
                                        <span className="font-medium break-words">{orgDisplayName(org)}</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                        <div className="bg-slate-50 p-2 border-t border-slate-100">
                            <NavLink 
                                to="/profile" 
                                onClick={() => setIsOrgMenuOpen(false)}
                                className="flex items-center justify-center w-full px-4 py-2 text-xs text-white bg-accent hover:bg-blue-600 rounded-md font-medium transition-colors"
                            >
                                + Добавить организацию
                            </NavLink>
                        </div>
                    </div>
                  )}
              </div>

              {/* User Info & Logout (Compact on Mobile) */}
              <div className="flex items-center gap-2 pl-2 sm:pl-4 border-l border-slate-200 flex-shrink-0">
                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600 border border-slate-200">
                    {user?.name.charAt(0)}
                </div>
                <button 
                    onClick={handleLogout}
                    className="p-2 text-slate-400 hover:text-red-600 transition-colors"
                    title="Выйти"
                >
                    <LogOut size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* MOBILE MENU (Выпадает снизу) */}
        {isMobileMenuOpen && (
            <div className="md:hidden border-t border-slate-200 bg-white absolute w-full left-0 z-10 shadow-lg">
                <nav className="flex flex-col p-4 space-y-2">
                    {navLinks.map(link => (
                        <NavLink 
                            key={link.to} 
                            to={link.to} 
                            end={link.end}
                            className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium ${isActive ? 'bg-accent text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                        >
                            {link.icon}
                            <span>{link.label}</span>
                        </NavLink>
                    ))}
                </nav>
            </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-hidden relative">
        <div className="h-full overflow-y-auto bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-20">
            {children}
          </div>
        </div>
      </main>
    </div>
  );
};